import { useState, type ReactNode } from 'react'
import { useTranslation } from 'next-i18next'
import { twMerge } from 'tailwind-merge'
import { HoverOverlay } from 'shared/components/hover-overlay'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import { useFloatHover } from 'shared/hooks/use-float-hover'
import useUser from 'shared/hooks/use-user'
import BellIcon from 'shared/icons/bell-icon'
import DoubleCheckmarkIcon from 'shared/icons/double-checkmark-icon'
import SettingsIcon from 'shared/icons/settings-icon'
import ThinArrowRightIcon from 'shared/icons/thin-arrow-right-icon'
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination'
import { isCustomDomain } from 'shared/utils/is-custom-domain'
import { getDashboardNotificationSettingsLink } from 'modules/header/menu/utils'
import NotificationItem from 'modules/profile/notifications/components/notification-item'
import NotificationEmptyList from 'modules/profile/notifications/components/notification-list-empty'
import NotificationsListSkeleton from 'modules/profile/notifications/components/notifications-list-skeleton'
import UnreadIndicator from 'modules/profile/notifications/components/unread-indicator'
import { useNotificationsActions } from 'modules/profile/notifications/hooks/use-notification-actions'
import { useNotifications } from 'modules/profile/notifications/hooks/use-notifications'

export interface NotificationMenuProps {
  className?: string
  isCustomer?: boolean
  iconClassname?: string
  activeMenuIconClassname?: string
}

const defaultFilter = {
  onlyUnRead: false,
}

const NotificationMenu = ({
  className,
  isCustomer = false,
  iconClassname,
  activeMenuIconClassname,
}: NotificationMenuProps): ReactNode => {
  const { t } = useTranslation()
  const { user } = useUser()
  const [shouldFetch, setShouldFetch] = useState(false)

  const { pagination } = useFilterPagination({
    defaultFilter,
    isStaticUrl: true,
    initLimit: 3,
  })
  const { notificationsMeta, notifications, isFetching } = useNotifications({
    shouldFetch,
    limit: 3,
    pagination,
    filter: defaultFilter,
  })
  const { markAllNotificationsAsRead } = useNotificationsActions()

  const { show, delayClose, delayOpen } = useFloatHover()

  return (
    <HoverOverlay
      portal
      show={show}
      placement="bottom-end"
      gap={5}
      className={twMerge('flex h-full items-center text-text-primary', className)}
      floatProps={{
        offset: {
          mainAxis: 5,
          crossAxis: 135,
        },
        enter: 'transition duration-100 ease-out',
        enterFrom: 'opacity-0',
        enterTo: 'opacity-100',
        leave: 'transition duration-75 ease-in',
        leaveFrom: 'opacity-100',
        leaveTo: 'opacity-0',
      }}
    >
      <button
        data-testid="notification-menu-trigger"
        onMouseEnter={() => {
          setShouldFetch(true)
          delayOpen()
        }}
        onMouseLeave={delayClose}
        className="flex h-full items-center justify-around gap-2.5 self-center border-b-4 border-transparent focus:outline-hidden focus-visible:border-brand-primary"
      >
        <div className="relative w-5 text-sm font-bold text-background-secondary/90">
          <BellIcon
            className={twMerge(
              `size-[18px] fill-background-secondary/80 stroke-background-secondary/80 transition hover:fill-background-secondary hover:stroke-background-secondary`,
              iconClassname,
              show &&
                twMerge(
                  `fill-background-secondary stroke-background-secondary`,
                  activeMenuIconClassname,
                ),
            )}
          />
          {notificationsMeta?.hasUnread && (
            <UnreadIndicator className="absolute top-0 right-0 p-0" iconClassName="size-2" />
          )}
        </div>
      </button>

      <div
        data-testid="notification-menu-panel"
        onMouseEnter={delayOpen}
        onMouseLeave={delayClose}
        className="flex w-[500px] flex-col rounded-lg bg-background-secondary py-4 text-sm shadow-lg"
      >
        <div className="flex flex-row justify-between px-6">
          <h4 className="text-[18px] text-text-primary">
            {t('profile.user_settings.notifications.title')}
          </h4>
          <div className="inline-flex flex-row items-baseline gap-1 stroke-text-primary text-text-primary transition hover:stroke-brand-primary hover:text-brand-primary">
            <DoubleCheckmarkIcon />
            <span className="cursor-pointer" onClick={markAllNotificationsAsRead}>
              {t('profile.user_settings.notifications.mark_all_read')}
            </span>
          </div>
        </div>
        {isFetching ? (
          <NotificationsListSkeleton />
        ) : notifications?.items.length ? (
          notifications?.items.map(notification => (
            <NotificationItem key={notification.id} notification={notification} />
          ))
        ) : (
          <NotificationEmptyList />
        )}
        <div className="flex justify-between px-6 pt-4">
          <LinkWithoutPrefetch
            className="inline-flex flex-row items-center gap-1 stroke-text-primary primary-link text-text-primary hover:stroke-brand-primary hover:text-brand-primary"
            href={
              isCustomer && !isCustomDomain
                ? getDashboardNotificationSettingsLink(user)
                : '/profile/notification-settings'
            }
          >
            <SettingsIcon width={18} height={18} />
            <span className="hidden sm:inline">
              {t('profile.user_settings.manage_notifications.title')}
            </span>
          </LinkWithoutPrefetch>
          <LinkWithoutPrefetch
            className="inline-flex flex-row items-center gap-3 stroke-text-primary primary-link text-text-primary hover:stroke-brand-primary hover:text-brand-primary"
            href="/profile/notifications"
          >
            <h4 className="capitalize">{t('profile.user_settings.notifications.view_all')}</h4>
            <ThinArrowRightIcon />
          </LinkWithoutPrefetch>
        </div>
      </div>
    </HoverOverlay>
  )
}

export default NotificationMenu
